/**
 * Workspace mutation actions — pure, immutable, and unit-test-friendly.
 *
 * These functions take a `UserWorkspace` and return a NEW `UserWorkspace` with
 * a single logical change applied. They are the exact mutations the future
 * QueVee tool-bridge will drive, so they live apart from React: no hooks, no
 * side effects, just immutable transforms over the contract model.
 *
 * The `workspaceReducer` at the bottom adapts them for `useReducer`.
 */

import type {
  Annotation,
  BriefBoardConfig,
  Dock,
  UserWorkspace,
  Layout,
  ChartGroup,
  Pane,
  Plot,
  PlotPalette,
  PlotStyle,
  Frequency,
  PlotType,
  ScreenerConfig,
  SyncFlags,
} from "@qufolio/model";
import { chartGroupConfig, screenerConfig } from "./section-config";
import { isMacroKind, withMacroSection, type MacroKind, type MacroSectionConfig } from "./macro-sections";
import { mintUlid } from "@qufolio/model";
import { stripLinkedConditions, stripLinks } from "@qufolio/screener-grid";
import {
  CHART_ALIASES,
  VOLUME_BAND,
  chartTypeChoice,
  pricePlot,
  volumeOverlaid,
  type ChartTypeChoice,
} from "./chart-group";
import { journalPosition, moveTab as moveTabOrder } from "./tab-strip";
import {
  withAdopted,
  withLegacyResolved,
  withAddedVersion,
  withLinked,
  withReplaced,
  withSourceSet,
  type Adopted,
  type SourceLink,
  type LegacyAnswer,
} from "./group-layouts";
import type { PipCorner, PipSize, PipView } from "./pip";
import { setPaneScale as setGroupPaneScale, type PaneScale } from "./pane-scale";
import { findChartGroup, isSymbolLocked } from "./symbol-lock";

// ── Immutable navigation helpers ────────────────────────────────────────────

function mapLayouts(
  ws: UserWorkspace,
  fn: (layout: Layout) => Layout,
): UserWorkspace {
  return { ...ws, layouts: ws.layouts.map(fn) };
}

function mapLayout(
  ws: UserWorkspace,
  layoutId: string,
  fn: (layout: Layout) => Layout,
): UserWorkspace {
  return mapLayouts(ws, (l) => (l.id === layoutId ? fn(l) : l));
}

/** Apply `fn` to the ChartGroup with the given id, wherever it lives. */
function mapChartGroup(
  ws: UserWorkspace,
  chartGroupId: string,
  fn: (group: ChartGroup) => ChartGroup,
): UserWorkspace {
  return mapLayouts(ws, (l) => ({
    ...l,
    sections: l.sections.map((s) => {
      const group = chartGroupConfig(s);
      return group && group.id === chartGroupId ? { ...s, config: fn(group) } : s;
    }),
  }));
}

/**
 * The chart groups a change to `chartGroupId` should reach.
 *
 * Just that one, unless its layout has the matching `sync` flag set — then every
 * chart in **that** layout, and no others. A layout is the unit because that is
 * what the flag is on: two charts you can see side by side are the ones you meant
 * to link, and a workspace-wide sync would change a chart on a tab you are not
 * looking at.
 *
 * **The fan-out belongs here rather than in the caller.** The toolbar, the
 * screener's row cursor and the agent tool-bridge all dispatch `setSymbol`, and
 * all three should stay unaware that sync exists — a caller-side fan-out is the
 * same rule written three times, drifting three ways. Putting it in the reducer
 * also keeps it to one save: the whole workspace goes back as one write however
 * many charts moved.
 */
function syncedGroupIds(
  ws: UserWorkspace,
  chartGroupId: string,
  flag: keyof SyncFlags,
): Set<string> {
  const layout = ws.layouts.find((l) =>
    l.sections.some((s) => chartGroupConfig(s)?.id === chartGroupId),
  );
  if (!layout?.sync?.[flag]) return new Set([chartGroupId]);
  return new Set(
    layout.sections
      .map((s) => chartGroupConfig(s)?.id)
      .filter((id): id is string => id !== undefined),
  );
}

/**
 * Apply `fn` to every chart group in `ids`.
 *
 * A layout holding none of them is returned **by identity**, not rebuilt. React
 * compares by reference, so a fresh object for an untouched layout re-renders
 * every chart on it — and a synced change already touches more of the tree than
 * an unsynced one, which is exactly when this matters most.
 */
function mapChartGroups(
  ws: UserWorkspace,
  ids: Set<string>,
  fn: (group: ChartGroup) => ChartGroup,
): UserWorkspace {
  return mapLayouts(ws, (l) => {
    let changed = false;
    const sections = l.sections.map((s) => {
      const group = chartGroupConfig(s);
      if (!group || !ids.has(group.id)) return s;
      // **Identity when `fn` changed nothing**, not merely when the id missed.
      // An action that decides it has no work — a toggle already in the asked-for
      // state — would otherwise mint a fresh section and layout, and a fresh
      // object for an untouched chart re-renders it. That is the same rule
      // `mapPlot` documents; this was matching on the id alone.
      const next = fn(group);
      if (next === group) return s;
      changed = true;
      return { ...s, config: next };
    });
    return changed ? { ...l, sections } : l;
  });
}

/**
 * Apply `fn` to the Plot with the given id, across every chart group / pane.
 *
 * Everything not on the path to that plot is returned **by identity** — the same
 * rule `mapChartGroups` follows, and for the same reason. This used to rebuild
 * every layout, section and pane on every call, which was survivable while the
 * only caller was a chart-type button clicked once in a while. The indicator
 * picker's style controls changed that: a width spinner fires this per keystroke,
 * and a fresh object for an untouched layout re-renders every chart on it.
 */
function mapPlot(
  ws: UserWorkspace,
  plotId: string,
  fn: (plot: Plot) => Plot,
): UserWorkspace {
  return mapLayouts(ws, (l) => {
    let layoutChanged = false;
    const sections = l.sections.map((s) => {
      const group = chartGroupConfig(s);
      if (!group) return s;

      let groupChanged = false;
      const panes = group.panes.map((pane) => {
        if (!pane.plots?.some((p) => p.id === plotId)) return pane;
        groupChanged = true;
        return { ...pane, plots: pane.plots.map((p) => (p.id === plotId ? fn(p) : p)) };
      });
      if (!groupChanged) return s;
      layoutChanged = true;
      return { ...s, config: { ...group, panes } };
    });
    return layoutChanged ? { ...l, sections } : l;
  });
}

// ── Pure mutation actions ───────────────────────────────────────────────────

/** Replace a layout's CSS-grid column sizes (`fr` strings). Drives grid resize. */
export function setColumnSizes(
  ws: UserWorkspace,
  layoutId: string,
  columnSizes: string[],
): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => ({ ...l, columnSizes }));
}

/** Replace a layout's CSS-grid row sizes (`fr` strings). Drives grid resize. */
export function setRowSizes(
  ws: UserWorkspace,
  layoutId: string,
  rowSizes: string[],
): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => ({ ...l, rowSizes }));
}

/**
 * Set which panels a layout's dock shows (`Layout.dock`).
 *
 * The caller computes the dock with `lib/dock.ts`; this only stores it. A
 * structural change, so it takes the full save — the panel list is not a drag.
 */
export function setDock(ws: UserWorkspace, layoutId: string, dock: Dock): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => ({ ...l, dock }));
}

/**
 * The divider inside the dock. A drag, so it has the drag's granular write.
 *
 * Ignored unless it fits the panels showing: a size for a pair that is no
 * longer there would otherwise be saved against the pair that replaced it.
 */
export function setDockSizes(ws: UserWorkspace, layoutId: string, sizes: string[]): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => {
    const dock = l.dock ?? { panels: ["screener" as const] };
    if (sizes.length !== dock.panels.length) return l;
    return { ...l, dock: { ...dock, sizes } };
  });
}

/**
 * Replace a layout with a new version of itself, matched by id.
 *
 * The caller computes the replacement — `applyTemplate` needs to *mint* the
 * sections a bigger arrangement adds, and minting inside a reducer would make it
 * impure and its result impossible to assert on. So the geometry happens in
 * `lib/layout-templates`, the ids in the panel, and this only swaps the answer
 * in.
 */
export function replaceLayout(ws: UserWorkspace, layout: Layout): UserWorkspace {
  return mapLayout(ws, layout.id, () => layout);
}

/** Append a layout. Selection is session state, so nothing here points at it. */
export function addLayout(ws: UserWorkspace, layout: Layout): UserWorkspace {
  return { ...ws, layouts: [...ws.layouts, layout] };
}

/**
 * Rename a layout.
 *
 * Duplicate names are not refused: tabs are selected by index and layouts carry
 * ULIDs, so two called "Charts" is confusing but not broken — and rejecting one
 * means blocking a rename halfway through typing. The caller refuses only the
 * empty name, which would leave the tab strip nothing to render.
 */
export function renameLayout(
  ws: UserWorkspace,
  layoutId: string,
  name: string,
): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => ({ ...l, name }));
}

/**
 * Hide a layout, or show it again.
 *
 * **`true` or absent, never `false`.** The contract states `hidden` as optional
 * where absent and false say the same thing, and qv-data collapses an empty
 * optional to absent — so writing `false` would make a save and the reload that
 * follows it differ. The same rule `setSync` follows one function below.
 *
 * Nothing here refuses to hide the last visible layout: that is the caller's
 * rule, because only the caller knows which are shown, and a reducer that
 * silently declined would be a write that reports success and does nothing.
 */
export function setLayoutHidden(
  ws: UserWorkspace,
  layoutId: string,
  hidden: boolean,
): UserWorkspace {
  return mapLayout(ws, layoutId, (l) => {
    if (!hidden) {
      const { hidden: _dropped, ...rest } = l;
      return rest as Layout;
    }
    return { ...l, hidden: true };
  });
}

/**
 * Drag one tab to another place in the strip.
 *
 * `from` and `to` are positions in `tabStrip`'s order — the layouts with the
 * journal among them — because that is the list the user is actually dragging
 * in. The arithmetic is `lib/tab-strip.ts`: one spliced array yields both the
 * new layout order *and* the journal's new position, so the two cannot disagree
 * about which way things travelled.
 *
 * **The journal's position is written even when it lands last.** Absent means
 * "last" only because a workspace saved before the field existed has to keep
 * the order it had; once the user has dragged, the position is a choice, and
 * dropping it as a default would move the journal again the next time a layout
 * was added. That is the one place this departs from the drop-the-default rule
 * the rest of this module follows, and the reason is that the default here is
 * `layouts.length` rather than a constant.
 *
 * No-ops return the workspace **by identity**, so a drop on the row you picked
 * up costs neither a re-render nor a save.
 */
export function moveTab(ws: UserWorkspace, from: number, to: number): UserWorkspace {
  const moved = moveTabOrder(ws.layouts, ws.journalIndex, from, to);
  const same =
    moved.journalIndex === journalPosition(ws.layouts, ws.journalIndex) &&
    moved.layouts.every((l, i) => l === ws.layouts[i]);
  if (same) return ws;
  return { ...ws, layouts: moved.layouts, journalIndex: moved.journalIndex };
}

/**
 * Put the journal tab away, or bring it back.
 *
 * `Layout.hidden` for the one tab that has no layout, and it follows the same
 * rule: `true` or absent, never `false`, because the store collapses an empty
 * optional and a stored `false` would make a save differ from its own reload.
 *
 * Hiding is the only way to put the journal away — it has no delete — which is
 * the whole reason it is offered on a tab the user cannot lose.
 */
export function setJournalHidden(ws: UserWorkspace, hidden: boolean): UserWorkspace {
  if (hidden) return { ...ws, journalHidden: true };
  const { journalHidden: _dropped, ...rest } = ws;
  return rest as UserWorkspace;
}

/**
 * Remove a layout.
 *
 * The first thing in this module that destroys workspace data, and the only
 * mutation with no inverse — `addLayout` puts a *different* layout back, with
 * different ids throughout. So the confirmation belongs to the caller and has to
 * name what goes: "this removes 12 charts" is a different decision from "this
 * removes one", which is the rule `chartsDropped` already applies to an
 * arrangement.
 *
 * Removing the last layout is refused here rather than by the caller, unlike
 * hiding: a workspace with no layouts has nothing to render and no control to
 * fix it with, where a hidden one is always still in the panel.
 */
export function removeLayout(ws: UserWorkspace, layoutId: string): UserWorkspace {
  if (ws.layouts.length <= 1) return ws;
  const layouts = ws.layouts.filter((l) => l.id !== layoutId);
  return layouts.length === ws.layouts.length ? ws : { ...ws, layouts };
}

/**
 * Merge sync flags into a layout's existing ones.
 *
 * Merged rather than replaced so one toggle can be sent on its own without the
 * caller having to restate the other three.
 *
 * **A flag that is off is dropped, and an empty result drops `sync` itself.**
 * The contract defaults every flag to false, so absent and false say the same
 * thing — and the store normalises an empty optional container to absent, so a
 * stored `{}` would come back `undefined` and make a workspace differ from its
 * own reload. That difference is invisible until something compares the two.
 *
 * **Switching one on harmonises the layout there and then**, from
 * `fromChartGroupId` — the selected chart. Recording the intent and waiting for
 * the next change was the behaviour before, and it reads as a broken tickbox:
 * you link two charts' intervals, nothing moves, and the only way to find out
 * it worked is to change an interval and watch. Linking is itself the
 * instruction that these charts should agree, so it makes them agree.
 *
 * Switching one **off** harmonises nothing, and that asymmetry is the point:
 * unlinking says "let these drift from here", not "put them back".
 *
 * The source is the selected chart because that is the one the user is looking
 * at and the one every other control in the strip already drives. With none
 * given — the agent bridge, a restored workspace — the layout's first chart
 * stands in, so the result is at least deterministic.
 */
export function setSync(
  ws: UserWorkspace,
  layoutId: string,
  sync: SyncFlags,
  fromChartGroupId?: string,
): UserWorkspace {
  const before: SyncFlags = ws.layouts.find((l) => l.id === layoutId)?.sync ?? {};
  const next = mapLayout(ws, layoutId, (l) => {
    const merged: SyncFlags = { ...l.sync, ...sync };
    for (const key of Object.keys(merged) as (keyof SyncFlags)[]) {
      // `crosshair` defaults ON, so it is the inverse: `false` is the value
      // worth keeping and `true` is the default, dropped like the others' off.
      if (key === "crosshair" ? merged[key] !== false : !merged[key]) delete merged[key];
    }
    const { sync: _dropped, ...rest } = l;
    return Object.keys(merged).length > 0 ? { ...rest, sync: merged } : rest;
  });

  const turnedOn = (Object.keys(sync) as (keyof SyncFlags)[]).filter(
    (key) => sync[key] && !before[key],
  );
  if (turnedOn.length === 0) return next;

  // Read back from `next`, not `ws`: every fan-out below decides its targets by
  // reading the flag it is named for, so the flags have to be written first.
  const groups = (next.layouts.find((l) => l.id === layoutId)?.sections ?? [])
    .map((s) => chartGroupConfig(s))
    .filter((g): g is ChartGroup => g !== undefined);
  const source = groups.find((g) => g.id === fromChartGroupId) ?? groups[0];
  // One chart is already in agreement with itself.
  if (!source || groups.length < 2) return next;

  return turnedOn.reduce((acc, key) => harmonize(acc, source, key), next);
}

/**
 * Make every chart in the source's layout agree with it about one property.
 *
 * Each arm is the ordinary mutation for that property, aimed at the source —
 * which is what keeps this honest: the flag is already on, so the mutation's own
 * fan-out does the spreading, and there is no second copy of "who does a synced
 * change reach" to drift from `syncedGroupIds`.
 */
function harmonize(
  ws: UserWorkspace,
  source: ChartGroup,
  flag: keyof SyncFlags,
): UserWorkspace {
  switch (flag) {
    case "symbol":
      return setSymbol(ws, source.id, source.symbol);
    case "frequency":
      return setFrequency(ws, source.id, source.frequency);
    case "barRange":
      // Both halves of the window, and each omitted when the source has none —
      // `setBarWindow` reads an absent key as "leave it alone", so passing
      // `undefined` explicitly would be the same thing said less clearly.
      return setBarWindow(ws, source.id, {
        ...(source.visibleBars !== undefined ? { visibleBars: source.visibleBars } : {}),
        ...(source.barsRange !== undefined ? { barsRange: source.barsRange } : {}),
      });
    // One action covers both: it writes the bar source and the plot type, and
    // since it now fans each out on its own flag, the half that was not linked
    // stops at the source.
    case "chartType":
    case "barSource": {
      const choice = chartTypeChoice(source);
      return choice ? setChartTypeChoice(ws, source.id, choice) : ws;
    }
    // A pointer position, not a stored property — nothing to bring into line.
    case "crosshair":
      return ws;
  }
}

/**
 * Set the symbol of a chart group — and of every chart synced with it.
 *
 * `sync.symbol` on its layout turns one click into a layout-wide change, which
 * is the whole point of a two-chart layout: the same name on two timeframes.
 *
 * **This is the symbol lock's guard** (`lib/symbol-lock.ts`, owner 2026-10-06),
 * and it is here because every path that changes a symbol ends here — the box,
 * the screener, the journal, a Community post, the agent bridge. A locked chart
 * asked for a *different* symbol is refused and the workspace comes back by
 * identity, so nothing re-renders and nothing saves. The fan-out skips locked
 * charts: they do not follow the link, unlocked ones in the layout still do.
 * A locked chart asked for the symbol it already has is not a change, so its
 * unlocked peers still follow — which is what ticking the link from a locked
 * chart (`harmonize`) needs.
 */
export function setSymbol(
  ws: UserWorkspace,
  chartGroupId: string,
  symbol: string,
): UserWorkspace {
  const target = findChartGroup(ws, chartGroupId);
  if (isSymbolLocked(target) && target!.symbol !== symbol) return ws;
  const ids = syncedGroupIds(ws, chartGroupId, "symbol");
  return mapChartGroups(ws, ids, (g) => (isSymbolLocked(g) ? g : { ...g, symbol }));
}

/**
 * Lock or unlock one chart's symbol. `true` or absent, never `false` — qv-data
 * emits only `true`, so storing `false` would make a save and its reload differ
 * (`setLayoutHidden`'s rule). Unlock stays unlocked: nothing re-locks on its own
 * (decision 1).
 */
export function setSymbolLocked(
  ws: UserWorkspace,
  chartGroupId: string,
  locked: boolean,
): UserWorkspace {
  return mapChartGroups(ws, new Set([chartGroupId]), (g) => withLock(g, locked));
}

/**
 * The layout panel's switch: lock, or unlock, every chart in one layout. One
 * action and so one save and one undo, however many charts it touches; a chart
 * already in the asked-for state comes back by identity.
 */
export function setLayoutSymbolLocked(
  ws: UserWorkspace,
  layoutId: string,
  locked: boolean,
): UserWorkspace {
  const layout = ws.layouts.find((l) => l.id === layoutId);
  const ids = new Set(
    (layout?.sections ?? [])
      .map((s) => chartGroupConfig(s)?.id)
      .filter((id): id is string => id !== undefined),
  );
  return mapChartGroups(ws, ids, (g) => withLock(g, locked));
}

function withLock(group: ChartGroup, locked: boolean): ChartGroup {
  if (isSymbolLocked(group) === locked && (locked || !("symbolLocked" in group))) return group;
  const { symbolLocked: _was, ...rest } = group;
  return locked ? { ...rest, symbolLocked: true } : rest;
}

/**
 * Replace one chart group outright — what applying a chart template does.
 *
 * **One action rather than a dozen**, because it is one gesture: one undo, one
 * save, and a workspace that never exists in a half-applied state where the
 * panes are the template's and the interval is still the old chart's.
 *
 * The caller computes the group (`lib/chart-template.ts`'s `applyTo`), because
 * minting ids is not something a reducer may do — `workspace-actions.test.ts`
 * pins that these functions are pure and structurally share what they do not
 * touch. This only swaps it in. `onReplace` for a layout makes the same split.
 *
 * **The symbol fans out if it moved**, exactly as `setSymbol` would have, so a
 * linked layout cannot end up with one chart on the template's symbol and the
 * rest on the old one. Only when it actually moved: fanning out a symbol that
 * did not change would rewrite every chart in the layout for nothing, which is
 * a workspace save and a refetch each.
 */
export function replaceChartGroup(
  ws: UserWorkspace,
  chartGroupId: string,
  group: ChartGroup,
): UserWorkspace {
  let moved = false;
  const swapped = mapChartGroup(ws, chartGroupId, (before) => {
    moved = before.symbol !== group.symbol;
    return group;
  });
  return moved ? setSymbol(swapped, chartGroupId, group.symbol) : swapped;
}

/**
 * Set the bar frequency of a chart group, and of every chart linked to it.
 *
 * Stored as asked. Until 2026-09-27 an intraday pick was folded to `1d` here,
 * because qv-data could not serve one (`served-frequency.ts`, since deleted —
 * docs/plans/intraday.md).
 */
export function setFrequency(
  ws: UserWorkspace,
  chartGroupId: string,
  frequency: Frequency,
): UserWorkspace {
  return mapChartGroups(ws, syncedGroupIds(ws, chartGroupId, "frequency"), (g) => ({
    ...g,
    frequency,
  }));
}

/**
 * Change a plot's renderable type (e.g. candlestick → line). Triggers a rebuild.
 *
 * Addressed by plot rather than by chart group, because that is what the control
 * holds. `sync.chartType` therefore has to find the *corresponding* plot on the
 * other charts, and "corresponding" is `pricePlot` — the same rule the toolbar
 * used to pick the plot being changed, so the two cannot disagree about which
 * series a chart type belongs to.
 *
 * Only the price plot syncs. An EMA drawn as a line on one chart and a
 * histogram on another is not a mismatch anybody is trying to fix, and syncing
 * every plot would change an indicator the other chart does not even have.
 *
 * **Which means the fan-out has to check that the plot IS the price plot.** The
 * indicator picker now sets a type per plot — MACD's histogram from a line to
 * columns — and a synced layout would otherwise have turned every *other*
 * chart's candles into columns, because the fan-out finds its target with
 * `pricePlot` regardless of what was changed here.
 */
export function setChartType(
  ws: UserWorkspace,
  plotId: string,
  type: PlotType,
): UserWorkspace {
  const owner = ws.layouts
    .flatMap((l) => l.sections)
    .map(chartGroupConfig)
    .find((g) => g?.panes.some((pane) => pane.plots?.some((p) => p.id === plotId)));
  if (!owner) return mapPlot(ws, plotId, (p) => ({ ...p, type }));
  if (pricePlot(owner)?.id !== plotId) return mapPlot(ws, plotId, (p) => ({ ...p, type }));

  const ids = syncedGroupIds(ws, owner.id, "chartType");
  if (ids.size <= 1) return mapPlot(ws, plotId, (p) => ({ ...p, type }));

  return mapChartGroups(ws, ids, (group) => {
    const target = group.id === owner.id ? plotId : pricePlot(group)?.id;
    if (!target) return group;
    return {
      ...group,
      panes: group.panes.map((pane) =>
        pane.plots
          ? { ...pane, plots: pane.plots.map((p) => (p.id === target ? { ...p, type } : p)) }
          : pane,
      ),
    };
  });
}

/**
 * Pick what the price looks like — the control's five options over two fields.
 *
 * Four of them are `Plot.type` on the price plot; Heikin-Ashi is
 * `ChartGroup.barSource`, because it re-forms every bar rather than redrawing
 * the same ones. One action rather than two dispatches, so switching away from
 * Heikin-Ashi is a single save and cannot leave a chart half-changed — on HA
 * bars with a line plot, say, which is a legal state nothing would have chosen.
 *
 * **The alias moves with it.** A candle reads its columns *by alias*
 * (`chart-feed`'s `derivedCandleColumns`), so the price plot is `haOhlc` on
 * Heikin-Ashi and `ohlc` otherwise. Left on `ohlc`, an HA chart would be handed
 * the base frame's real prices and draw ordinary candles — the right shape, the
 * wrong numbers, and no error.
 *
 * Heikin-Ashi forces the plot to `candlestick`: it is a *bar*, and a bar drawn
 * as a line is just its close, which `haClose` already says more plainly.
 *
 * Finds its target with `pricePlot` for `setChartType`'s reason — two rules for
 * "which plot is the price" would eventually disagree.
 *
 * **Two fields, two flags, and they fan out separately.** One dispatch writes
 * both, but a layout that links "Chart type" and not "Bar source" asked for
 * exactly one of them to travel, and the reverse holds too. This fanned out on
 * `sync.barSource` alone, which made the layout panel's "Chart type" tickbox do
 * nothing at all — the toolbar's control is this action, and `setChartType`,
 * the one that reads that flag, is reached only from the indicator picker — and
 * made "Bar source" carry plot types it has no business carrying.
 *
 * **The alias and the forced candlestick belong to the bar-source half**, not
 * the type half. A chart moved onto HA without its alias is handed the base
 * frame's real prices and draws the right shape over the wrong numbers, so the
 * two must not be separable by a flag.
 *
 * A target already on Heikin-Ashi is skipped by the *type* half for the same
 * reason the originator is: HA is a bar, and a bar drawn as a line is just its
 * close. Linking chart type therefore cannot quietly break a chart whose bar
 * source was deliberately left unlinked.
 */
/**
 * The price line in two colours (owner, 2026-10-03): positive where the close
 * rose — `close > close[1]` — negative otherwise. Written onto the plot when the
 * price becomes a line, so the indicator editor can still change or remove it;
 * taken off when it becomes anything else, **but only if it is this default** —
 * a candle colours itself, and a palette left on one would recolour it by the
 * close's direction instead of open-to-close.
 */
export const TWO_TONE_LINE: PlotPalette = {
  upColor: "positive",
  downColor: "negative",
  colorMode: "delta",
};

/**
 * The price plot's palette for its new type.
 *
 * **A new chart's price is born `{positive, negative, colorMode: "sign"}`**
 * (`chart-group.ts`). A candle takes only the two colours from it, so the mode
 * never mattered — until the price became a line, where `sign` means "up when
 * above zero" and a price is always above zero: every segment drew the up
 * colour (found 2026-10-04 by probing the owner's chart). So the mode follows
 * the type, and the colours are kept:
 *
 * - **line**: `delta` — up where the close rose. No palette at all gets the
 *   default two-tone.
 * - **anything else**: a two-colour palette goes back to `sign`, which is what
 *   candles are born with.
 *
 * A single colour or a `colorBy` somebody chose is left alone either way.
 */
function withLinePalette(plot: Plot): Plot {
  const palette = plot.palette;
  const twoTone = !!palette && !!(palette.upColor || palette.downColor) && !palette.colorBy;
  if (plot.type === "line") {
    if (!palette) return { ...plot, palette: { ...TWO_TONE_LINE } };
    return twoTone && palette.colorMode !== "delta"
      ? { ...plot, palette: { ...palette, colorMode: "delta" } }
      : plot;
  }
  return twoTone && palette!.colorMode === "delta"
    ? { ...plot, palette: { ...palette!, colorMode: "sign" } }
    : plot;
}

export function setChartTypeChoice(
  ws: UserWorkspace,
  groupId: string,
  choice: ChartTypeChoice,
): UserWorkspace {
  const heikinAshi = choice === "heikinAshi";
  const barsIds = syncedGroupIds(ws, groupId, "barSource");
  const typeIds = syncedGroupIds(ws, groupId, "chartType");
  // Both always contain the chart that was clicked, so it alone gets both
  // halves whatever is linked.
  const targets = new Set([...barsIds, ...typeIds]);

  return mapChartGroups(ws, targets, (group) => {
    const bars = barsIds.has(group.id);
    const draws = typeIds.has(group.id);
    const price = pricePlot(group);

    // `barSource` dropped rather than written as "time": the contract defaults
    // it, and storing the default would make every chart look as though someone
    // had chosen it — the same rule `gridLines` and the rest follow.
    const { barSource: _drop, ...rest } = group;
    const next: ChartGroup = bars
      ? heikinAshi
        ? { ...rest, barSource: "heikinAshi" }
        : rest
      : group;
    if (!price) return next;

    const patch: { type?: PlotType; outputAlias?: string } = {};
    if (bars) {
      patch.outputAlias = heikinAshi ? CHART_ALIASES.haOhlc : CHART_ALIASES.ohlc;
      if (heikinAshi) patch.type = "candlestick";
    }
    if (draws && !heikinAshi && next.barSource !== "heikinAshi") {
      patch.type = choice as PlotType;
    }
    if (Object.keys(patch).length === 0) return next;

    return {
      ...next,
      panes: next.panes.map((pane) =>
        pane.plots
          ? {
              ...pane,
              plots: pane.plots.map((p) =>
                p.id === price.id ? withLinePalette({ ...p, ...patch }) : p,
              ),
            }
          : pane,
      ),
    };
  });
}

/**
 * Draw volume **on** the price pane, or give it a pane of its own.
 *
 * A restructure rather than a flag, so the layout stays the one source of truth
 * — `volumeOverlaid` reads it back from the panes for `matchTemplate`'s reason:
 * a stored boolean is falsified by the first pane edit that disagrees with it.
 *
 * The overlay is a **hidden, banded axis** on the price pane. Hidden matters
 * twice: it takes no width, so the pane is not inset a second time and its bars
 * stay aligned with every other pane's; and a volume scale printed beside a
 * price scale is two sets of numbers on one edge, neither labelled. Banded to
 * the bottom fifth so the bars sit under the candles instead of across them —
 * `bandedRange` does the arithmetic, in the Y fit, because `growBy` is ignored
 * on an axis that is handed its range.
 *
 * Turning it off mints a fresh pane and axis. The old ids are gone, which is
 * correct: they are the identity of a pane that no longer exists, and reusing
 * them would make an undo look like a resize to the persistence layer.
 */
export function setVolumeOverlay(
  ws: UserWorkspace,
  groupId: string,
  overlay: boolean,
): UserWorkspace {
  return mapChartGroups(ws, new Set([groupId]), (group) => {
    if (volumeOverlaid(group) === overlay) return group;
    const price = group.panes.find((p) => p.role === "price");
    if (!price) return group;

    if (overlay) {
      const source = group.panes.find(
        (p) => p !== price && (p.plots ?? []).some((x) => x.outputAlias === CHART_ALIASES.volume),
      );
      const plot = (source?.plots ?? []).find((x) => x.outputAlias === CHART_ALIASES.volume);
      if (!source || !plot) return group;
      const axisId = mintUlid();
      return {
        ...group,
        panes: group.panes
          .filter((p) => p.id !== source.id)
          .map((p) =>
            p.id === price.id
              ? {
                  ...p,
                  axes: [
                    ...(p.axes ?? []),
                    { id: axisId, side: "right", autoRange: "always", visible: false,
                      band: { ...VOLUME_BAND } } as NonNullable<Pane["axes"]>[number],
                  ],
                  // Ordered first so it draws *under* the candles. Painted over
                  // them, a 0.8-opacity column hides the very bars it annotates.
                  plots: [{ ...plot, axisRef: axisId, order: -1 }, ...(p.plots ?? [])],
                }
              : p,
          ),
      };
    }

    const plot = (price.plots ?? []).find((x) => x.outputAlias === CHART_ALIASES.volume);
    if (!plot) return group;
    const axisId = mintUlid();
    const overlayAxes = new Set(
      (price.axes ?? []).filter((a) => a.visible === false).map((a) => a.id),
    );
    return {
      ...group,
      panes: [
        ...group.panes.map((p) =>
          p.id === price.id
            ? {
                ...p,
                axes: (p.axes ?? []).filter((a) => !overlayAxes.has(a.id)),
                plots: (p.plots ?? []).filter((x) => x.id !== plot.id),
              }
            : p,
        ),
        {
          id: mintUlid(),
          role: "volume",
          order: group.panes.length,
          height: "1fr",
          axes: [{ id: axisId, side: "right", autoRange: "always", scale: "linear",
                   format: "0,0", grid: { minorsPerMajor: 2 } }],
          plots: [{ ...plot, axisRef: axisId, order: 0 }],
          annotations: [],
        } as NonNullable<ChartGroup["panes"]>[number],
      ],
    };
  });
}

/**
 * Show or hide one plot.
 *
 * Workspace data, not view state (D8): which of an indicator's outputs you want
 * on the chart is a choice about the chart, and one that would be tedious to
 * remake every session — Bollinger with its middle line off is a setup, not a
 * glance. It is a contract field for exactly that reason.
 *
 * `false` is **written rather than dropped**, because the contract defaults
 * `hidden` to false and qv-data shreds a plot into named columns: deleting the
 * key on unhide would work, but storing it keeps the round trip honest and
 * matches how `axisDrag` is handled for the same reason.
 */
export function setPlotHidden(
  ws: UserWorkspace,
  plotId: string,
  hidden: boolean,
): UserWorkspace {
  return mapPlot(ws, plotId, (p) => ({ ...p, hidden }));
}

/**
 * Show or hide several plots as **one** gesture — the value legend's eye on a
 * family heading, which hides Bollinger's three lines *and* the fill between
 * them. One action rather than a dispatch per plot: one undo, one save.
 */
export function setPlotsHidden(
  ws: UserWorkspace,
  plotIds: readonly string[],
  hidden: boolean,
): UserWorkspace {
  return plotIds.reduce((acc, plotId) => setPlotHidden(acc, plotId, hidden), ws);
}

/** Merge new style properties into a plot's style. */
export function setPlotStyle(
  ws: UserWorkspace,
  plotId: string,
  style: PlotStyle,
): UserWorkspace {
  return mapPlot(ws, plotId, (p) => ({ ...p, style: { ...p.style, ...style } }));
}

/**
 * Merge new palette properties into a plot's palette.
 *
 * Separate from `setPlotStyle` because they are separate contract nodes, not
 * because they are separately edited: `style` is the stroke, and `palette` is
 * the three-tier colouring — a sign-coloured histogram is `upColor` /
 * `downColor` / `colorMode`, none of which `PlotStyle` can say.
 *
 * A key set to `undefined` is deleted rather than stored, so dropping back from
 * up/down to a single colour leaves no `colorMode` behind for the render plan to
 * find. Same rule `updateScreener` follows, for the same reason.
 */
export function setPlotPalette(
  ws: UserWorkspace,
  plotId: string,
  palette: PlotPalette,
): UserWorkspace {
  return mapPlot(ws, plotId, (p) => {
    const next: PlotPalette = { ...p.palette, ...palette };
    for (const key of Object.keys(palette) as (keyof PlotPalette)[]) {
      if (palette[key] === undefined) delete next[key];
    }
    return { ...p, palette: next };
  });
}

/**
 * Merge a patch into a screener section's config.
 *
 * The grid reports column/filter/sort changes as contract-shaped fragments; the
 * workspace — not the grid — stays the source of truth, so the same state
 * replays on any renderer. A key set to `undefined` (a cleared filter) is
 * deleted rather than stored, keeping the persisted config free of null holes.
 */
/**
 * What may be patched on a screener section.
 *
 * **Named once, because it was written out twice** — on the function and again
 * on the action — and two copies of a key list are two things to remember when
 * one gains a member. `screenRef` was the member that found it.
 */
export type ScreenerPatch = Partial<
  Pick<
    ScreenerConfig,
    | "screenRef"
    | "columns"
    | "filter"
    | "sort"
    | "activeFilterRef"
    | "activeFilterRefs"
    // The conditions picker's category order (↑↓ on a group header), and what
    // a screen with its own library brings with it (`applyScreenTo`).
    | "savedFilterCategoryOrder"
    | "savedFilters"
    // A JP trade-list screen: what a Recs screen brings, and its D/W/M and side.
    | "recs"
    // A TopDown screen: what it brings, and the panel's ticks and layout.
    | "topDown"
    // A Seasonality screen: what it brings, and the panel's bar.
    | "seasonality"
    // The bar's ▲▼ key, and what a screen brings (`applyScreenTo`).
    | "direction"
  >
>;

export function updateScreener(
  ws: UserWorkspace,
  sectionId: string,
  patch: ScreenerPatch,
): UserWorkspace {
  return mapLayouts(ws, (l) => ({
    ...l,
    sections: l.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const config = screenerConfig(s);
      if (!config) return s;

      const next: ScreenerConfig = { ...config, ...patch };
      for (const key of Object.keys(patch) as (keyof typeof patch)[]) {
        if (patch[key] === undefined) delete next[key];
      }
      // **Links are stored as links** (`docs/plans/screen-links.md`). The grid
      // and the builder draw the materialised config and patch back what they
      // drew; this is the one door every edit to a section comes through, so a
      // source's header, format or rule can never be saved into the member's
      // copy — where it would go stale the day the group changed it.
      if (next.columns) next.columns = stripLinks(next.columns);
      if (next.savedFilters) next.savedFilters = stripLinkedConditions(next.savedFilters);
      return { ...s, config: next };
    }),
  }));
}

/**
 * Replace a macro section's whole config (`lib/macro-sections.ts`) — the
 * Daily Brief's rule (whole, not patched) for its reason. Applies to one of
 * the five macro kinds and nothing else, so a stale id can never write a
 * calendar's settings into a chart or a board.
 */
export function setMacroSectionConfig(
  ws: UserWorkspace,
  sectionId: string,
  config: MacroSectionConfig,
): UserWorkspace {
  const hit = (s: Layout["sections"][number]) => s.id === sectionId && isMacroKind(s.kind);
  return mapLayouts(ws, (l) =>
    l.sections.some(hit)
      ? { ...l, sections: l.sections.map((s) => (hit(s) ? { ...s, config } : s)) }
      : l,
  );
}

/**
 * + Add section: a macro section appended to the layout as a new bottom row
 * (`withMacroSection`). A kind the layout already holds is not added twice.
 */
export function addMacroSection(
  ws: UserWorkspace,
  layoutId: string,
  kind: MacroKind,
): UserWorkspace {
  const at = ws.layouts.findIndex((l) => l.id === layoutId);
  if (at < 0) return ws;
  const next = withMacroSection(ws.layouts[at]!, kind);
  return next === ws.layouts[at] ? ws : mapLayout(ws, layoutId, () => next);
}

/**
 * Replace a Daily Brief section's whole config (`docs/plans/daily-brief.md`).
 *
 * Whole, not patched: the board's editors (`lib/brief-board.ts`) already return
 * the finished config — preset and `mine` together — so a patch would only be
 * a second copy of their rules. Applies to a `briefBoard` section and nothing
 * else, so a stale id can never write a board into a chart or a screener.
 */
export function setSectionConfig(
  ws: UserWorkspace,
  sectionId: string,
  config: BriefBoardConfig,
): UserWorkspace {
  return mapLayouts(ws, (l) =>
    l.sections.some((s) => s.id === sectionId && s.kind === "briefBoard")
      ? {
          ...l,
          sections: l.sections.map((s) =>
            s.id === sectionId && s.kind === "briefBoard" ? { ...s, config } : s,
          ),
        }
      : l,
  );
}

/**
 * Point the workspace at a theme.
 *
 * The *selected* theme is workspace data (`themeRef`, D13) — there is no
 * parallel theme store, so a saved workspace reopens in the theme it was left
 * in. Nothing else in the model changes: colors are semantic tokens resolved at
 * render time, so switching is a repaint, not a migration of every stored color.
 */
export function setTheme(ws: UserWorkspace, themeRef: string): UserWorkspace {
  return { ...ws, themeRef };
}

/**
 * Set the gridline pattern for a chart group.
 *
 * Lives on the ChartGroup rather than the theme because a theme is *picked* from
 * a fixed set — there is no `themes[]` collection to persist an authored one
 * into — so a user could not change this if it were a token. Colour stays a
 * token (D13); only the pattern is workspace data, and it applies to both
 * directions because the X axis has no per-axis config to override it with.
 */
export function setGridLines(
  ws: UserWorkspace,
  chartGroupId: string,
  gridLines: NonNullable<ChartGroup["gridLines"]>,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, gridLines }));
}

/**
 * Show or hide the value legend for a chart group.
 *
 * Stored rather than held in session state (D8): it is a preference about a
 * chart, and one that has to survive a reload like `gridLines` and `axisDrag`
 * beside it. Not a `SyncFlags` entry — those fan a persisted field *between*
 * charts, and what one chart draws in its own corner is not something the chart
 * next to it has an opinion about.
 *
 * `false` is written rather than the key dropped, for `setAxisDrag`'s reason:
 * the contract default is `true`, so an absent field means shown, and deleting
 * it would silently turn back on what was turned off.
 */
/**
 * Continue the chart's indicators past the last bar, over projected closes.
 *
 * `true` is written and `false` **dropped**, which is the opposite of
 * `setValueLegend` and for the same underlying rule: the contract defaults this
 * to `false`, so an absent field already means off, and storing that would make
 * every chart look as though somebody had considered projection and declined.
 * qv-data collapses an absent optional back to absent, so writing `false` would
 * also make a save and its reload differ.
 */
export function setProjectForward(
  ws: UserWorkspace,
  chartGroupId: string,
  projectForward: boolean,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, ({ projectForward: _was, ...g }) =>
    projectForward ? { ...g, projectForward: true } : g,
  );
}

export function setValueLegend(
  ws: UserWorkspace,
  chartGroupId: string,
  valueLegend: boolean,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, valueLegend }));
}

/**
 * Turn a chart's earnings or news markers on or off — saved per chart (owner,
 * 2026-10-08; view state in `App` until then).
 *
 * `false` is written and `true` **dropped**, `setProjectForward`'s rule turned
 * round: the contract defaults both to on, so an absent field already means on,
 * and qv-data collapses an absent optional back to absent.
 */
export function setChartEvents(
  ws: UserWorkspace,
  chartGroupId: string,
  events: { earnings?: boolean; news?: boolean },
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => {
    const { showEarnings, showNews, ...rest } = g;
    const earnings = events.earnings ?? showEarnings ?? true;
    const news = events.news ?? showNews ?? true;
    return {
      ...rest,
      ...(earnings ? {} : { showEarnings: false }),
      ...(news ? {} : { showNews: false }),
    };
  });
}

/**
 * Fin St's years or quarters for this chart — saved (owner, 2026-10-08).
 * `"years"` is the contract default and is dropped rather than stored.
 */
export function setFinStGrain(
  ws: UserWorkspace,
  chartGroupId: string,
  grain: "years" | "quarters",
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, ({ finStGrain: _was, ...g }) =>
    grain === "quarters" ? { ...g, finStGrain: "quarters" } : g,
  );
}

/**
 * Turn the picture-in-picture panel on, off, or on to a particular view.
 *
 * **`pipView` is what "there is a PIP" means.** A PIP is a view — the higher
 * timeframe, the profile, peers — so there is nothing to store when there is
 * none, and a separate boolean would be a second source of truth for one fact.
 * Turning it off therefore *deletes* the key rather than writing a value.
 *
 * **The other three settings survive being turned off.** Someone who put the
 * panel bottom-left at size `l` and closed it wants it back where they left it,
 * and re-choosing a corner every time is the kind of small tax that stops a
 * feature being used. They are meaningless while `pipView` is absent, so
 * nothing reads them in the meantime.
 */
export function setPipView(
  ws: UserWorkspace,
  chartGroupId: string,
  view: PipView | null,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => {
    if (view) return { ...g, pipView: view };
    const { pipView: _closed, ...rest } = g;
    return rest;
  });
}

/**
 * Pin the PIP's interval, or clear the pin.
 *
 * Clearing is a real choice rather than a reset: absent means "the next rung
 * above whatever this chart shows", which keeps following the chart as its own
 * interval changes. A pinned value stops doing that until it is no longer
 * coarser, at which point `pipFrequencyFor` promotes it on read — see `pip.ts`.
 */
export function setPipFrequency(
  ws: UserWorkspace,
  chartGroupId: string,
  frequency: Frequency | null,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => {
    if (frequency) return { ...g, pipFrequency: frequency };
    const { pipFrequency: _cleared, ...rest } = g;
    return rest;
  });
}

/**
 * Where the PIP sits, or `auto` to let the placement rule decide.
 *
 * `auto` is **written rather than dropped**, for `setAxisDrag`'s reason in
 * reverse: it is also the contract default, so dropping it would be
 * indistinguishable from never having chosen — which is fine today and would
 * stop being fine the moment the default changes. One value, one meaning.
 */
export function setPipCorner(
  ws: UserWorkspace,
  chartGroupId: string,
  corner: PipCorner,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, pipCorner: corner }));
}

/** How large the PIP is drawn, as one of three named sizes. */
export function setPipSize(
  ws: UserWorkspace,
  chartGroupId: string,
  size: PipSize,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, pipSize: size }));
}

/**
 * Turn drag-an-axis-to-scale on or off for a chart group.
 *
 * Stored, not toggled at runtime, for the same reason `gridLines` is (D8): it is
 * a preference about a chart, so it has to survive a reload. `false` is written
 * rather than dropped — the contract's default is `true`, so an absent field
 * means on, and deleting the key would silently re-enable what was turned off.
 */
export function setAxisDrag(
  ws: UserWorkspace,
  chartGroupId: string,
  axisDrag: boolean,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, axisDrag }));
}

/**
 * Set how many bars a chart shows, and — only when a span needs it — how many it
 * loads.
 *
 * **One action rather than two**, because a preset moves both together: showing
 * a year of five-minute bars is a window *and* a bigger request, and dispatching
 * them separately would put a render between them in which the chart shows a
 * year's worth of window over a fortnight's worth of data. It is also one save
 * instead of two.
 *
 * `barsRange` is left alone when omitted, which is the ordinary case — the count
 * box moves the window over bars that are already here, and that must never cost
 * a request. See `lib/chart-window.ts` for which of the two a span needs.
 *
 * Fans out on `sync.barRange`, the flag the layout panel calls "Date range".
 * Two charts side by side with it on are the pair you linked precisely so that
 * scrolling one scrolls the other.
 */
export function setBarWindow(
  ws: UserWorkspace,
  chartGroupId: string,
  window: { visibleBars?: number; barsRange?: number },
): UserWorkspace {
  return mapChartGroups(ws, syncedGroupIds(ws, chartGroupId, "barRange"), (g) => ({
    ...g,
    ...(window.visibleBars !== undefined ? { visibleBars: window.visibleBars } : {}),
    ...(window.barsRange !== undefined ? { barsRange: window.barsRange } : {}),
  }));
}

/**
 * Set the empty space kept to the right of the last bar.
 *
 * Not synced, unlike the window above: it is an appearance setting like
 * `gridLines`, and linking two charts' *date ranges* says nothing about how much
 * air either of them leaves at the edge.
 */
export function setRightMargin(
  ws: UserWorkspace,
  chartGroupId: string,
  rightMargin: number,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, rightMargin }));
}

/** Which direction the wheel zooms and a drag pans, for a chart group. */
export function setZoomAxis(
  ws: UserWorkspace,
  chartGroupId: string,
  zoomAxis: NonNullable<ChartGroup["zoomAxis"]>,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, zoomAxis }));
}

/**
 * What the price axis scales to: every plot on it, or the bars alone.
 *
 * A `ChartGroup` field beside `gridLines` and `zoomAxis`, so it saves through
 * the ordinary full-save path and comes back on reload — which needed a column
 * in qv-data (`0010`) before it could be added here at all. The store shreds a
 * `ChartGroup` into named columns and drops a field it has no column for,
 * silently, and this is the fifth field that would have gone that way.
 */
export function setPriceFit(
  ws: UserWorkspace,
  chartGroupId: string,
  priceFit: NonNullable<ChartGroup["priceFit"]>,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, priceFit }));
}

/**
 * Pin an indicator pane's Y axis to a range, or let it fit what is on screen.
 *
 * The renderer and the store have both supported this since they were written —
 * `createYAxis` treats an axis as pinned unless it is `autoRange: "always"` with
 * no range, and qv-data has had `auto_range`, `visible_min` and `visible_max`
 * all along. This is the action that finally lets a user say it.
 *
 * The rule that a range and `always` cannot coexist lives in `withPaneScale`,
 * not here: it is the part with a failure mode (an axis refitted on the next
 * frame looks like a control that did nothing) and therefore the part worth
 * testing without React.
 */
export function setPaneScale(
  ws: UserWorkspace,
  chartGroupId: string,
  paneId: string,
  scale: PaneScale,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => setGroupPaneScale(g, paneId, scale));
}

/**
 * Set every pane height in a chart group, positionally.
 *
 * Positional rather than per-pane-id because a divider moves *two* panes at
 * once: the pair either side of the seam. Writing them separately would leave
 * the model briefly summing to the wrong total, and the renderer normalises by
 * the sum.
 */
/**
 * Add a drawing to a pane.
 *
 * Only ever the **user's** drawings reach this. The trading plan's levels and
 * the fill marks are computed per render and must never be written here — a
 * derived line that got saved is a number with two homes, and editing the plan
 * would leave the stored copy rendering correctly and wrong.
 * `chart-engine`'s `persistable()` is the guard on the way back out.
 */
export function addAnnotation(
  ws: UserWorkspace,
  chartGroupId: string,
  paneId: string,
  annotation: Annotation,
): UserWorkspace {
  // **A highlight on a bar already highlighted takes it off** (owner,
  // 2026-10-13, `docs/plans/bar-highlights.md`): the same click toggles, rather
  // than stacking two columns nobody can tell apart.
  if (annotation.type === "barHighlight") {
    const group = findChartGroup(ws, chartGroupId);
    const pane = group?.panes?.find((p) => p.id === paneId);
    const twin =
      group && pane
        ? annotationsFor(pane.annotations, group).find(
            (a) => a.type === "barHighlight" && a.x1 === annotation.x1,
          )
        : undefined;
    if (twin) return removeAnnotation(ws, chartGroupId, paneId, twin.id);
  }
  return mapChartGroup(ws, chartGroupId, (g) => ({
    ...g,
    panes: g.panes.map((p) =>
      p.id === paneId
        ? {
            ...p,
            annotations: [
              ...(p.annotations ?? []),
              // **Stamped here, from the chart, rather than by the caller.**
              // A drawing describes one instrument at one interval, and the
              // surface that produced it knows the pointer coordinates and
              // nothing else — so the one place that holds both the chart and
              // the drawing is the one that can say which. A caller that
              // already stamped it is left alone, which is what lets a template
              // carry drawings across without them being re-stamped onto the
              // symbol they landed on.
              annotation.symbol === undefined
                ? { ...annotation, symbol: g.symbol, frequency: g.frequency }
                : annotation,
            ],
          }
        : p,
    ),
  }));
}

/**
 * The drawings on a pane that belong on the chart as it stands.
 *
 * **Absent means everywhere**, which is what every drawing made before the
 * stamp existed did — so nothing a user already has disappears, and the rule
 * needs no migration to be true.
 */
export function annotationsFor(
  annotations: readonly Annotation[] | undefined,
  group: Pick<ChartGroup, "symbol" | "frequency">,
): Annotation[] {
  return (annotations ?? []).filter(
    (a) =>
      (a.symbol === undefined || a.symbol === group.symbol) &&
      (a.frequency === undefined || a.frequency === group.frequency),
  );
}

/** Remove one drawing, by the id it was minted with. */
export function removeAnnotation(
  ws: UserWorkspace,
  chartGroupId: string,
  paneId: string,
  annotationId: string,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({
    ...g,
    panes: g.panes.map((p) =>
      p.id === paneId
        ? { ...p, annotations: (p.annotations ?? []).filter((a) => a.id !== annotationId) }
        : p,
    ),
  }));
}

/**
 * Restyle one drawing — colour, weight, dash, and a text's size.
 *
 * **A patch, unlike a plot's style, and deliberately.** `setPlotStyle` takes a
 * plot's *whole* style because merging cannot express "no dash" there: a plot has
 * one style object and an absent key is indistinguishable from "leave it alone".
 * A drawing is edited one control at a time from a bar that is showing the
 * current values, so the caller genuinely means "this key, that value" — and
 * clearing a dash is a control setting `dash: []`, which a patch can say.
 *
 * The pane is not passed. A drawing is selected on the chart, not in a pane, and
 * the selection reports an id and nothing else; asking the caller which pane it
 * came from would be asking it to re-derive something the renderer already knew
 * and dropped. Ids are ULIDs, so scanning the panes for one is unambiguous.
 *
 * Only the user's own drawings can reach this: a derived level is not in
 * `Pane.annotations`, so there is nothing here to patch, which is the same guard
 * that keeps the plan's lines out of the workspace in the first place.
 */
export function styleAnnotation(
  ws: UserWorkspace,
  chartGroupId: string,
  annotationId: string,
  style: NonNullable<Annotation["style"]>,
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({
    ...g,
    panes: g.panes.map((p) =>
      (p.annotations ?? []).some((a) => a.id === annotationId)
        ? {
            ...p,
            annotations: (p.annotations ?? []).map((a) =>
              a.id === annotationId ? { ...a, style: { ...a.style, ...style } } : a,
            ),
          }
        : // Identity for a pane that does not hold it, so the panes that were not
          // touched keep theirs and stop re-rendering — the reason `mapPlot` was
          // narrowed when a width spinner started firing per keystroke.
          p,
    ),
  }));
}

/**
 * Every drawing off every pane of one chart.
 *
 * The whole chart rather than one pane, because that is what the ⌫ button in
 * the tool strip means — a user who drew on the volume pane by accident is
 * looking for the button that undoes it, not for which pane it landed on.
 *
 * The key is dropped rather than set to `[]`: qv-data collapses an empty
 * container to absent, so an empty array saved would come back missing and a
 * save would differ from its own reload.
 */
export function clearAnnotations(ws: UserWorkspace, chartGroupId: string): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({
    ...g,
    panes: g.panes.map(({ annotations: _drop, ...pane }) => pane),
  }));
}

export function setPaneHeights(
  ws: UserWorkspace,
  chartGroupId: string,
  heights: readonly string[],
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) =>
    g.panes.length === heights.length
      ? { ...g, panes: g.panes.map((p, i) => ({ ...p, height: heights[i]! })) }
      : g,
  );
}

/**
 * Replace a chart group's panes wholesale.
 *
 * The caller computes them — adding an indicator mints a pane, an axis and a
 * plot per output, and minting inside a reducer would make it impure and its
 * result impossible to assert on. So the geometry happens in
 * `lib/add-indicator`, the ids in the component, and this only swaps the answer
 * in. Exactly the precedent `replaceLayout` sets.
 *
 * Panes rather than the whole group, because `symbol`, `frequency` and
 * `gridLines` are edited from three other controls and a whole-group action
 * would carry a stale copy of each of them into the store.
 */
export function setPanes(
  ws: UserWorkspace,
  chartGroupId: string,
  panes: Pane[],
): UserWorkspace {
  return mapChartGroup(ws, chartGroupId, (g) => ({ ...g, panes }));
}

// ── Reducer adapter for React `useReducer` ──────────────────────────────────

export type WorkspaceAction =
  | { type: "setColumnSizes"; layoutId: string; columnSizes: string[] }
  | { type: "setRowSizes"; layoutId: string; rowSizes: string[] }
  | { type: "setDock"; layoutId: string; dock: Dock }
  | { type: "setDockSizes"; layoutId: string; sizes: string[] }
  | { type: "replaceLayout"; layout: Layout }
  | { type: "addLayout"; layout: Layout }
  /** A member's group layouts, copied in hidden — see `lib/group-layouts`. */
  | { type: "adoptLayouts"; copies: readonly Adopted[] }
  /** Group layout copies republished since — swapped in place. */
  | { type: "replaceLayouts"; copies: readonly Adopted[] }
  /** A group layout applied from the panel — shown, and linked like an adopted one. */
  | { type: "linkLayout"; copy: Adopted }
  /** A new version taken beside the member's copy, which is unlinked and kept. */
  | { type: "addLayoutVersion"; copy: Adopted }
  /** Author as: the group account's layouts linked to the version they are. */
  | { type: "linkSources"; links: readonly SourceLink[] }
  /** The one-time question about an unlinked same-name copy, answered. */
  | {
      type: "resolveLegacyLayout";
      legacy: { key: string; mineId: string; redundantIds: readonly string[] };
      copy: Adopted;
      answer: LegacyAnswer;
      groupName: string;
    }
  | { type: "renameLayout"; layoutId: string; name: string }
  | { type: "setLayoutHidden"; layoutId: string; hidden: boolean }
  | { type: "removeLayout"; layoutId: string }
  | { type: "setSync"; layoutId: string; sync: SyncFlags; fromChartGroupId?: string }
  | { type: "moveTab"; from: number; to: number }
  | { type: "setJournalHidden"; hidden: boolean }
  | { type: "setSymbol"; chartGroupId: string; symbol: string }
  | { type: "setSymbolLocked"; chartGroupId: string; locked: boolean }
  | { type: "setLayoutSymbolLocked"; layoutId: string; locked: boolean }
  | { type: "setFrequency"; chartGroupId: string; frequency: Frequency }
  | { type: "replaceChartGroup"; chartGroupId: string; group: ChartGroup }
  | { type: "setChartType"; plotId: string; chartType: PlotType }
  | { type: "setChartTypeChoice"; groupId: string; choice: ChartTypeChoice }
  | { type: "setVolumeOverlay"; groupId: string; overlay: boolean }
  | { type: "setPlotHidden"; plotId: string; hidden: boolean }
  | { type: "setPlotsHidden"; plotIds: string[]; hidden: boolean }
  | { type: "setPlotStyle"; plotId: string; style: PlotStyle }
  | { type: "setPlotPalette"; plotId: string; palette: PlotPalette }
  | { type: "setPaneHeights"; chartGroupId: string; heights: string[] }
  | {
      type: "addAnnotation";
      chartGroupId: string;
      paneId: string;
      annotation: Annotation;
    }
  | {
      type: "removeAnnotation";
      chartGroupId: string;
      paneId: string;
      annotationId: string;
    }
  | {
      type: "styleAnnotation";
      chartGroupId: string;
      annotationId: string;
      style: NonNullable<Annotation["style"]>;
    }  | { type: "clearAnnotations"; chartGroupId: string }
  | { type: "setPanes"; chartGroupId: string; panes: Pane[] }
  | { type: "setTheme"; themeRef: string }
  | {
      type: "setGridLines";
      chartGroupId: string;
      gridLines: NonNullable<ChartGroup["gridLines"]>;
    }
  | { type: "setAxisDrag"; chartGroupId: string; axisDrag: boolean }
  | { type: "setValueLegend"; chartGroupId: string; valueLegend: boolean }
  | {
      type: "setChartEvents";
      chartGroupId: string;
      events: { earnings?: boolean; news?: boolean };
    }
  | { type: "setFinStGrain"; chartGroupId: string; grain: "years" | "quarters" }
  | { type: "setProjectForward"; chartGroupId: string; projectForward: boolean }
  | { type: "setPipView"; chartGroupId: string; view: PipView | null }
  | { type: "setPipFrequency"; chartGroupId: string; frequency: Frequency | null }
  | { type: "setPipCorner"; chartGroupId: string; corner: PipCorner }
  | { type: "setPipSize"; chartGroupId: string; size: PipSize }
  | {
      type: "setBarWindow";
      chartGroupId: string;
      visibleBars?: number;
      barsRange?: number;
    }
  | { type: "setRightMargin"; chartGroupId: string; rightMargin: number }
  | {
      type: "setZoomAxis";
      chartGroupId: string;
      zoomAxis: NonNullable<ChartGroup["zoomAxis"]>;
    }
  | {
      type: "setPriceFit";
      chartGroupId: string;
      priceFit: NonNullable<ChartGroup["priceFit"]>;
    }
  | {
      type: "setPaneScale";
      chartGroupId: string;
      paneId: string;
      scale: PaneScale;
    }
  | {
      type: "updateScreener";
      sectionId: string;
      patch: ScreenerPatch;
    }
  | { type: "setSectionConfig"; sectionId: string; config: BriefBoardConfig }
  | { type: "setMacroSectionConfig"; sectionId: string; config: MacroSectionConfig }
  | { type: "addMacroSection"; layoutId: string; kind: MacroKind }
  | { type: "reset"; workspace: UserWorkspace };

export function workspaceReducer(
  ws: UserWorkspace,
  action: WorkspaceAction,
): UserWorkspace {
  switch (action.type) {
    case "setColumnSizes":
      return setColumnSizes(ws, action.layoutId, action.columnSizes);
    case "setRowSizes":
      return setRowSizes(ws, action.layoutId, action.rowSizes);
    case "setDock":
      return setDock(ws, action.layoutId, action.dock);
    case "setDockSizes":
      return setDockSizes(ws, action.layoutId, action.sizes);
    case "replaceLayout":
      return replaceLayout(ws, action.layout);
    case "addLayout":
      return addLayout(ws, action.layout);
    case "adoptLayouts":
      return withAdopted(ws, action.copies);
    case "replaceLayouts":
      return withReplaced(ws, action.copies);
    case "linkLayout":
      return withLinked(ws, action.copy);
    case "addLayoutVersion":
      return withAddedVersion(ws, action.copy);
    case "linkSources":
      return withSourceSet(ws, action.links);
    case "resolveLegacyLayout":
      return withLegacyResolved(ws, action.legacy, action.copy, action.answer, action.groupName);
    case "renameLayout":
      return renameLayout(ws, action.layoutId, action.name);
    case "setLayoutHidden":
      return setLayoutHidden(ws, action.layoutId, action.hidden);
    case "removeLayout":
      return removeLayout(ws, action.layoutId);
    case "setSync":
      return setSync(ws, action.layoutId, action.sync, action.fromChartGroupId);
    case "moveTab":
      return moveTab(ws, action.from, action.to);
    case "setJournalHidden":
      return setJournalHidden(ws, action.hidden);
    case "setSymbol":
      return setSymbol(ws, action.chartGroupId, action.symbol);
    case "setSymbolLocked":
      return setSymbolLocked(ws, action.chartGroupId, action.locked);
    case "setLayoutSymbolLocked":
      return setLayoutSymbolLocked(ws, action.layoutId, action.locked);
    case "replaceChartGroup":
      return replaceChartGroup(ws, action.chartGroupId, action.group);
    case "setFrequency":
      return setFrequency(ws, action.chartGroupId, action.frequency);
    case "setChartType":
      return setChartType(ws, action.plotId, action.chartType);
    case "setChartTypeChoice":
      return setChartTypeChoice(ws, action.groupId, action.choice);
    case "setVolumeOverlay":
      return setVolumeOverlay(ws, action.groupId, action.overlay);
    case "setPlotHidden":
      return setPlotHidden(ws, action.plotId, action.hidden);
    case "setPlotsHidden":
      return setPlotsHidden(ws, action.plotIds, action.hidden);
    case "setPlotStyle":
      return setPlotStyle(ws, action.plotId, action.style);
    case "setPlotPalette":
      return setPlotPalette(ws, action.plotId, action.palette);
    case "addAnnotation":
      return addAnnotation(ws, action.chartGroupId, action.paneId, action.annotation);
    case "removeAnnotation":
      return removeAnnotation(ws, action.chartGroupId, action.paneId, action.annotationId);
    case "styleAnnotation":
      return styleAnnotation(ws, action.chartGroupId, action.annotationId, action.style);
    case "clearAnnotations":
      return clearAnnotations(ws, action.chartGroupId);
    case "setPaneHeights":
      return setPaneHeights(ws, action.chartGroupId, action.heights);
    case "setPanes":
      return setPanes(ws, action.chartGroupId, action.panes);
    case "setTheme":
      return setTheme(ws, action.themeRef);
    case "setGridLines":
      return setGridLines(ws, action.chartGroupId, action.gridLines);
    case "setAxisDrag":
      return setAxisDrag(ws, action.chartGroupId, action.axisDrag);
    case "setValueLegend":
      return setValueLegend(ws, action.chartGroupId, action.valueLegend);
    case "setChartEvents":
      return setChartEvents(ws, action.chartGroupId, action.events);
    case "setFinStGrain":
      return setFinStGrain(ws, action.chartGroupId, action.grain);
    case "setProjectForward":
      return setProjectForward(ws, action.chartGroupId, action.projectForward);
    case "setPipView":
      return setPipView(ws, action.chartGroupId, action.view);
    case "setPipFrequency":
      return setPipFrequency(ws, action.chartGroupId, action.frequency);
    case "setPipCorner":
      return setPipCorner(ws, action.chartGroupId, action.corner);
    case "setPipSize":
      return setPipSize(ws, action.chartGroupId, action.size);
    case "setBarWindow":
      return setBarWindow(ws, action.chartGroupId, {
        visibleBars: action.visibleBars,
        barsRange: action.barsRange,
      });
    case "setRightMargin":
      return setRightMargin(ws, action.chartGroupId, action.rightMargin);
    case "setZoomAxis":
      return setZoomAxis(ws, action.chartGroupId, action.zoomAxis);
    case "setPriceFit":
      return setPriceFit(ws, action.chartGroupId, action.priceFit);
    case "setPaneScale":
      return setPaneScale(ws, action.chartGroupId, action.paneId, action.scale);
    case "updateScreener":
      return updateScreener(ws, action.sectionId, action.patch);
    case "setSectionConfig":
      return setSectionConfig(ws, action.sectionId, action.config);
    case "setMacroSectionConfig":
      return setMacroSectionConfig(ws, action.sectionId, action.config);
    case "addMacroSection":
      return addMacroSection(ws, action.layoutId, action.kind);
    case "reset":
      return action.workspace;
  }
}